import {AdapterDateFns} from "@mui/x-date-pickers/AdapterDateFnsV3"
import {
	DateCalendar,
	type DateCalendarProps,
	LocalizationProvider,
	PickersDay,
	type PickersDayProps,
} from "@mui/x-date-pickers"
import {styled} from "@mui/material/styles"
import {differenceInCalendarDays} from "date-fns"

import {ValidLocale} from "../../src/Locales"

import {locales} from "./locales"

function sameDay(d1: Date, d2: Date) {
	return d1.getFullYear() === d2.getFullYear() && d1.getMonth() === d2.getMonth() && d1.getDate() === d2.getDate()
}

function Calendar({
	locale,
	minDate,
	maxDate,
	...props
}: {locale: ValidLocale; minDate: Date; maxDate: Date} & DateCalendarProps<Date>) {
	const renderDay = (day: Date, _selectedDays: Date[], props: PickersDayProps<Date>) => {
		const {key, ...restProps} = props
		if (
			differenceInCalendarDays(day, minDate) < 0 ||
			differenceInCalendarDays(day, maxDate) > 0 ||
			props.outsideCurrentMonth
		) {
			return <DisabledPickersDay key={key} {...restProps} />
		}

		return (
			<DayContainer key={key} $isLastDay={sameDay(day, maxDate)} $isFirstDay={sameDay(day, minDate)}>
				<CustomPickersDay {...restProps} />
			</DayContainer>
		)
	}
	return (
		<LocalizationProvider adapterLocale={locales[locale]} dateAdapter={AdapterDateFns}>
			<DateCalendar<Date>
				{...props}
				minDate={minDate}
				maxDate={maxDate}
				slots={{
					day: (dayProps) => renderDay(dayProps.day, [], dayProps),
				}}
				disableHighlightToday={true}
			/>
		</LocalizationProvider>
	)
}

const DisabledPickersDay = styled(PickersDay<Date>)`
	border-radius: 0;
`

const CustomPickersDay = styled(PickersDay<Date>)`
	color: ${(props) => (props.selected ? "white" : props.theme.palette.common.black)} !important;
	border-color: ${(props) => props.theme.palette.primary.main} !important;
	background-color: ${(props) => (props.selected ? props.theme.palette.primary.main : "#EDEFF1")} !important;
	border-radius: 50%;
`

const DayContainer = styled("div")<{$isLastDay: boolean; $isFirstDay: boolean; selected?: boolean}>`
	background-color: ${(props) => (props.selected ? props.theme.palette.primary.main : "#EDEFF1")};

	/* RTL-aware border radius */
	border-top-right-radius: ${(props) => {
		const isRtl = props.theme.direction === "rtl"
		if (isRtl) {
			return props.$isFirstDay ? "50%" : "0"
		} else {
			return props.$isLastDay ? "50%" : "0"
		}
	}};

	border-bottom-right-radius: ${(props) => {
		const isRtl = props.theme.direction === "rtl"
		if (isRtl) {
			return props.$isFirstDay ? "50%" : "0"
		} else {
			return props.$isLastDay ? "50%" : "0"
		}
	}};

	border-top-left-radius: ${(props) => {
		const isRtl = props.theme.direction === "rtl"
		if (isRtl) {
			return props.$isLastDay ? "50%" : "0"
		} else {
			return props.$isFirstDay ? "50%" : "0"
		}
	}};

	border-bottom-left-radius: ${(props) => {
		const isRtl = props.theme.direction === "rtl"
		if (isRtl) {
			return props.$isLastDay ? "50%" : "0"
		} else {
			return props.$isFirstDay ? "50%" : "0"
		}
	}};
`

export {Calendar}
